/**
 * DocumentProcessingBanner
 * v1.0 - ITEM-43: Post-upload processing banner on Dashboard - 2026-07-14
 *
 * Polls GET /api/ingest/document/list and surfaces per-document processing state
 * as a dismissible top-strip banner. State survives page reload via re-query (no
 * browser storage). §4.16: copy is plain language, no jargon, no money-first.
 *
 * Exported pure helpers (statusLabel, isInFlight, isTerminalSuccess,
 * isTerminalError, filterBannerItems) are unit-testable without a DOM.
 *
 * Shows documents created in the last 24 hours that are in-flight or terminal.
 * Polls every 8 seconds while any item is in-flight; stops when all settle.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  Close,
  ErrorOutline,
  HourglassEmpty,
  Sync,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ingestService, DocumentListItem } from '../services/api';
// Pure helpers live in utils file (no api.ts dependency) so they can be tested in isolation.
import {
  statusLabel,
  isInFlight,
  isTerminalSuccess,
  isTerminalError,
  filterBannerItems,
} from './documentProcessingBannerUtils';
// Re-export so callers can import helpers directly from this module.
export {
  statusLabel,
  isInFlight,
  isTerminalSuccess,
  isTerminalError,
  filterBannerItems,
} from './documentProcessingBannerUtils';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const POLL_MS = 8_000;

const DocumentProcessingBanner: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<DocumentListItem[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchList = useCallback(async () => {
    try {
      const res = await ingestService.listDocuments();
      setItems(filterBannerItems(res.documents ?? []));
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Start/stop polling based on whether any item is in-flight.
  useEffect(() => {
    const anyInFlight = items.some((i) => isInFlight(i.status));
    if (anyInFlight && !pollRef.current) {
      pollRef.current = setInterval(fetchList, POLL_MS);
    } else if (!anyInFlight && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [items, fetchList]);

  if (dismissed || loadError || items.length === 0) return null;

  const inFlightCount = items.filter((i) => isInFlight(i.status)).length;
  const bannerTitle =
    inFlightCount > 0
      ? `${inFlightCount} document${inFlightCount !== 1 ? 's' : ''} being prepared`
      : 'Documents ready';

  const hasFailed = items.some((i) => isTerminalError(i.status));
  const severity = hasFailed ? 'warning' : inFlightCount > 0 ? 'info' : 'success';

  return (
    <Collapse in sx={{ mb: 2 }}>
      <Alert
        severity={severity}
        action={
          <IconButton
            size="small"
            color="inherit"
            aria-label="Dismiss document status banner"
            onClick={() => setDismissed(true)}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <AlertTitle>{bannerTitle}</AlertTitle>
        <List dense disablePadding sx={{ mt: 0.5 }}>
          {items.map((item) => (
            <ListItem key={item.session_id} disableGutters sx={{ py: 0.25 }}>
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                {isInFlight(item.status) && item.status === 'queued' && (
                  <HourglassEmpty fontSize="small" color="disabled" />
                )}
                {isInFlight(item.status) && item.status === 'processing' && (
                  <Sync fontSize="small" color="info" />
                )}
                {isTerminalSuccess(item.status) && (
                  <CheckCircleOutline fontSize="small" color="success" />
                )}
                {isTerminalError(item.status) && (
                  <ErrorOutline fontSize="small" color="warning" />
                )}
              </Box>
              <ListItemText
                primary={
                  <Typography variant="body2" component="span">
                    <strong>{item.filename}</strong>
                    {' — '}
                    <span>{statusLabel(item.status)}</span>
                    {item.error_message && isTerminalError(item.status) && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ mt: 0.25 }}
                      >
                        {/* Show backend error message only if it's short and plain-text; otherwise use generic copy. */}
                        {item.error_message.length <= 120 && !/[{}[\]\\]/.test(item.error_message)
                          ? item.error_message
                          : 'Our system was unable to read this file. Check that it is a supported format and try again.'}
                      </Typography>
                    )}
                  </Typography>
                }
              />
              {isTerminalSuccess(item.status) && (
                <Chip
                  label="View"
                  size="small"
                  color="success"
                  variant="outlined"
                  clickable
                  onClick={() => navigate('/dashboard/data')}
                  sx={{ ml: 1 }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </Alert>
    </Collapse>
  );
};

export default DocumentProcessingBanner;
